/**
 * Dashboard Tile Container - Wrapper für draggable Kacheln
 */

import { useDrag, useDrop } from 'react-dnd';
import { useRef } from 'react';
import { GripVertical, Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const TILE_TYPE = 'DASHBOARD_TILE';

export default function DashboardTile({ 
  id, 
  index, 
  size = 'large',
  isEditing = false,
  onMove,
  onSizeChange,
  children,
  className
}) {
  const ref = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: TILE_TYPE,
    item: { id, index },
    canDrag: isEditing,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: TILE_TYPE,
    hover: (item, monitor) => {
      if (!ref.current || !isEditing) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      onMove?.(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drag(drop(ref));

  // Größen-Klassen
  const sizeClasses = {
    compact: 'col-span-1',
    large: 'col-span-1 lg:col-span-1',
    full: 'col-span-1 lg:col-span-2',
  };

  return (
    <div
      ref={preview}
      className={cn(
        sizeClasses[size],
        isDragging && 'opacity-50',
        isOver && 'ring-2 ring-blue-400 ring-offset-2',
        'transition-all duration-200',
        className
      )}
    >
      <div
        ref={ref}
        className={cn(
          'bg-white rounded-xl border border-slate-200 shadow-sm h-full',
          isEditing && 'ring-1 ring-dashed ring-slate-300',
          isDragging && 'shadow-lg'
        )}
      >
        {/* Edit Mode Header */}
        {isEditing && (
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-xl">
            <div className="flex items-center gap-2 cursor-move" ref={ref}>
              <GripVertical className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-500">Ziehen zum Verschieben</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onSizeChange?.(id, 'compact')}
                className={cn(
                  'p-1 rounded',
                  size === 'compact' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:text-slate-600'
                )}
                title="Kompakt"
              >
                <Minimize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onSizeChange?.(id, 'large')}
                className={cn(
                  'p-1 rounded',
                  size === 'large' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:text-slate-600'
                )}
                title="Groß"
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => onSizeChange?.(id, 'full')}
                className={cn(
                  'p-1.5 rounded text-[10px] font-bold',
                  size === 'full' ? 'bg-blue-100 text-blue-600' : 'text-slate-400 hover:text-slate-600'
                )}
                title="Volle Breite"
              >
                2x
              </button>
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
